import React, { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import { Tabs, Tab } from "react-bootstrap";
import { config, encodeBase64RequestBody, priceFormat, shortenUrl } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const ClientDetail = () => {
  const { id_company } = useParams();
  const [company, setCompany] = useState(null);
  const [timelines, setTimelines] = useState(null);
  const [subcompanies, setSubcompanies] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [quotations, setQuotations] = useState(null);
  const [ious, setIous] = useState(null);
  const [licences, setLicences] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [message, setMessage] = useState('');
  const [attachmentList, setAttachmentList] = useState([]);

  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  useEffect(() => {
    getCompany();
  }, []);

  useEffect(() => {
    if (company) {
      getTimelines();
      getSubcompanies();
      getContacts();
      getQuotations();
      getIous();
      getLicences();
      getContracts();
    }
  }, [company]);

  const getCompany = async () => {
    try {
      const requestGetCompany = {
        "id_company": id_company
      }
      const fetchGetCompany = await jwtFetch(config.ENDPOINT + 'get/company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: encodeBase64RequestBody(requestGetCompany)
      });
      const responseGetCompany = await fetchGetCompany.json();
      if (responseGetCompany.status === 200) {
        setCompany(responseGetCompany.data[0]);
      } else {
        alert(responseGetCompany.message);
      }
    } catch (error) {
      console.error('Error in getCompany:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getTimelines = async () => {
    try {
      const requestGetTimelines = {
        "company_id": id_company
      }
      const fetchGetTimelines = await jwtFetch(config.ENDPOINT + 'get/timeline', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestGetTimelines)
      });
      const responseGetTimelines = await fetchGetTimelines.json();
      if (responseGetTimelines.status === 200) {
        const timelineData = Array.isArray(responseGetTimelines.data) ? responseGetTimelines.data : [];
        setTimelines([...timelineData].sort((a, b) => new Date(b.created_on) - new Date(a.created_on)));
      } else {
        alert(responseGetTimelines.message);
      }
    } catch (error) {
      console.error('Error in getTimelines:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getSubcompanies = async () => {
    try {
      const requestGetSubcompany = {
        "company_id": id_company
      }
      const fetchGetSubcompany = await jwtFetch(config.ENDPOINT + 'get/subcompany', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: encodeBase64RequestBody(requestGetSubcompany)
      });
      const responseGetSubcompany = await fetchGetSubcompany.json();
      if (responseGetSubcompany.status === 200) {
        setSubcompanies([...responseGetSubcompany.data].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '')));
      } else {
        alert(responseGetSubcompany.message);
      }
    } catch (error) {
      console.error('Error in getSubcompanies:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getContacts = async () => {
    try {
      const requestGetContact = {
        "company_id": id_company
      }
      const fetchGetContact = await jwtFetch(config.ENDPOINT + 'get/contact_person', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: encodeBase64RequestBody(requestGetContact)
      });
      const responseGetContact = await fetchGetContact.json();
      if (responseGetContact.status === 200) {
        setContacts([...responseGetContact.data].sort((a, b) => (a.cperson_name || '').localeCompare(b.cperson_name || '')));
      } else {
        alert(responseGetContact.message);
      }
    } catch (error) {
      console.error('Error in getContacts:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getQuotations = async () => {
    try {
      const requestGetQuotations = {
        "company_id": id_company
      }
      const fetchGetQuotations = await jwtFetch(config.ENDPOINT + 'get/quotation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestGetQuotations)
      });
      const responseGetQuotations = await fetchGetQuotations.json();
      if (responseGetQuotations.status === 200) {
        setQuotations(responseGetQuotations.data);
      } else {
        alert(responseGetQuotations.message);
      }
    } catch (error) {
      console.error('Error in getQuotations:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIous = async () => {
    try {
      const requestGetIous = {
        "company_id": id_company
      }
      const fetchGetIous = await jwtFetch(config.ENDPOINT + 'get/iou', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestGetIous)
      });
      const responseGetIou = await fetchGetIous.json();
      if (responseGetIou.status === 200) {
        setIous(
          responseGetIou.data
            .filter((iou) => {
              const isUpcoming = (new Date(iou.start_term_period) >= new Date()) && (new Date(iou.start_term_period) <= addDays(new Date(), 7));
              const isOverdue = new Date(iou.start_term_period) <= new Date();
              return (iou.iou_status === 'New' || iou.iou_status === 'In Progress') && (isUpcoming || isOverdue);
            })
            .sort((a, b) => (a.start_term_period || '').localeCompare(b.start_term_period || '')));
      } else {
        alert(responseGetIou.message);
      }
    } catch (error) {
      console.error('Error in getIous:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getLicences = async () => {
    try {
      const requestGetLicences = {
        "company_id": id_company
      }
      const fetchGetLicences = await jwtFetch(config.ENDPOINT + 'get/clients_company_licence', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestGetLicences)
      });
      const responseGetLicence = await fetchGetLicences.json();
      if (responseGetLicence.status === 200) {
        setLicences(responseGetLicence.data.filter((licence) => licence.licence_status === 'Active'));
      } else {
        alert(responseGetLicence.message);
      }
    } catch (error) {
      console.error('Error in getLicences:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getContracts = async () => {
    try {
      const requestGetContracts = {
        "company_id": id_company
      }
      const fetchGetContracts = await jwtFetch(config.ENDPOINT + 'get/contract', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestGetContracts)
      });
      const responseGetContracts = await fetchGetContracts.json();
      if (responseGetContracts.status === 200) {
        setContracts(responseGetContracts.data);
      } else {
        alert(responseGetContracts.message);
      }
    } catch (error) {
      console.error('Error in getContracts:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const addAttachments = () => {
    const attachment = {
      name: '',
      files: '',
      loading: false
    };
    setAttachmentList([...attachmentList, attachment]);
  }

  const changeAttachmentData = (column, value, index) => {
    const attachment = attachmentList[index];
    attachment[column] = value;
    if (column === 'files') {
      attachment.loading = false;
    }
    setAttachmentList([...attachmentList]);
  }

  const uploadAttachments = async (files, index) => {
    changeAttachmentData('loading', true, index);
    const formData = new FormData();
    files.forEach((attachment) => {
      formData.append('files', attachment);
    });
    try {
      const responseUploadAttachments = await jwtFetch(config.ENDPOINT + 'upload/gdrive', {
        method: 'POST',
        body: formData,
      });
      if (!responseUploadAttachments.ok) {
        throw new Error(`Upload failed with status: ${responseUploadAttachments.status}`);
      }
      const resultUploadAttachments = await responseUploadAttachments.json();
      if (resultUploadAttachments.status === 200) {
        changeAttachmentData('files', resultUploadAttachments.data, index);
      } else {
        alert(`Upload error: ${resultUploadAttachments.message}`);
      }
    } catch (error) {
      alert(`Upload error: ${error.message || error}`);
    } finally {
      changeAttachmentData('loading', false, index);
    }
  }

  const removeAttachments = (index) => {
    attachmentList.splice(index, 1);
    setAttachmentList([...attachmentList]);
  }

  const postMessage = async (e) => {
    try {
      e.preventDefault();
      const requestPostMessage = {
        "company_id": id_company,
        "subcompany_id": null,
        "contact_person_id": null,
        "event_id": null,
        "event_name": "Timeline",
        "event_code": null,
        "event_status": null,
        "timeline_action": "Post",
        "timeline_title": "posted a message",
        "timeline_message": message,
        "timeline_files": attachmentList.length > 0 ? JSON.stringify(attachmentList) : null,
        "timeline_link": null,
        "created_on": format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        "created_by": crmUser.user_keyid,
      }
      const fetchPostMessage = await jwtFetch(config.ENDPOINT + 'create/timeline', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPostMessage)
      });
      const responsePostMessage = await fetchPostMessage.json();
      if (responsePostMessage.status === 200) {
        setMessage('');
        setAttachmentList([]);
        getTimelines();
      } else {
        alert(responsePostMessage.message);
      }
    } catch (error) {
      console.error('Error in postMessage:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  return (
    <div id="client-detail">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
            </div>
            <hr className="m-0" />
            <Menu active="client" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">

                <section className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <a className="text-secondary" href="/client">Client List</a>
                    <span className="mx-3"><i className="bi bi-chevron-right" /></span>
                    <span>Company Group Detail</span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="fs-24">Company Group Detail</div>
                  </div>
                  {company ? (
                    <div className="row mb-4">
                      <div className="col-md-8">
                        <div className="bg-white border-radius-4 p-4">
                          {((crmUser.user_department !== 'Sales Admin' && crmUser.user_level !== 'View' && crmUser.user_level !== 'View')) && (
                            <div className="d-flex align-items-center gap-2 mb-4">
                              <button className="btn bg-gold" type="button" onClick={() => window.location.href = '/client/edit/' + id_company}><i className="bi bi-pencil me-1" /> Edit</button>
                            </div>
                          )}
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Group Name</div>
                            <div className="col-md-8">{company.company_name}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Legal Name</div>
                            <div className="col-md-8">{company.company_billing_name}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Domain</div>
                            <div className="col-md-8">{company.company_domain} <a href={`https://${company.company_domain}`} target="_blank" rel="noreferrer"><i className="bi bi-link-45deg ms-2" /></a></div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Status</div>
                            <div className="col-md-8"><span className={"badge bg-" + (company.company_status === 'Active' ? 'success' : 'danger')}>{company.company_status}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p>No data</p>
                  )}
                </section>

                <section className="mb-4">
                  <Tabs className="mb-3" defaultActiveKey="timeline">
                    <Tab eventKey="timeline" title="Timeline">
                      <form className="mt-4 mb-4 p-4 border border-1 border-radius-3" onSubmit={postMessage}>
                        <textarea className="form-control mb-2" rows="3" placeholder="Post a message..." value={message} onChange={(e) => setMessage(e.target.value)} />
                        <div className="d-flex flex-column">
                          {attachmentList.length > 0 && (
                            <div className="table-responsive">
                              <table className="table table-bordered mb-2">
                                {/* <thead>
                                  <tr>
                                    <th className="bg-secondary text-light" style={{textAlign: 'center'}}>#</th>
                                    <th className="bg-secondary text-light w-400">Name</th>
                                    <th className="bg-secondary text-light">File</th>
                                    <th className="bg-secondary text-light"></th>
                                  </tr>
                                </thead> */}
                                <tbody>
                                  {attachmentList.map((attachment, a) =>
                                    <tr key={"attachment-" + a}>
                                      <td valign="middle" align="center">{a + 1}</td>
                                      <td valign="middle">
                                        <input className="form-control" type="text" value={attachment.name} placeholder="Description" onChange={(e) => changeAttachmentData('name', e.target.value, a)} required />
                                      </td>
                                      <td valign="middle">
                                        {attachment.loading === false ? (
                                          <div>
                                            {attachment.files ? (
                                              <a className="ellipsis" href={attachment.files[0].fileUrl} target="_blank" rel="noopener noreferrer">{shortenUrl(attachment.files[0].fileUrl, 50)} <i className="bi bi-link-45deg ms-2" /></a>
                                            ) : (
                                              <input className="form-control" type="file" accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => uploadAttachments(Array.from(e.target.files), a)} required />
                                            )}
                                          </div>
                                        ) : 'Loading...'}
                                      </td>
                                      <td valign="middle" align="center">
                                        <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeAttachments(a)}><i className="bi bi-trash" /></button>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        {(crmUser.user_department !== 'Sales Admin' && crmUser.user_level !== 'View') && (
                          <div className="d-flex align-items-center justify-content-between">
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addAttachments()}><i className="bi bi-plus-lg me-2" /> Attachment</button>
                            <button className="btn bg-gold" type="submit"><i className="bi bi-send me-2" /> Post</button>
                          </div>
                        )}
                      </form>
                      {timelines && (
                        <div className="timeline-container">
                          {timelines.map((timeline, index) => {
                            const currentDate = new Date(timeline.created_on);
                            const prevDate = index > 0 ? new Date(timelines[index - 1].created_on) : null;
                            const showDate = !prevDate || currentDate.toDateString() !== prevDate.toDateString();

                            const getIcon = (table) => {
                              if (table === 'Timeline') { return 'bi-chat-text'; }
                              else if (table === 'Lead') { return 'bi-crosshair'; }
                              else if (table === 'Opportunity') { return 'bi-gem'; }
                              else if (table === 'Quotation') { return 'bi-file-earmark-text'; }
                              else if (table === 'SalesOrder') { return 'bi-cart-check'; }
                              else if (table === 'InvoiceRequest') { return 'bi-receipt-cutoff'; }
                              else if (table === 'Product') { return 'bi-box-seam'; }
                              else if (table === 'Contact') { return 'bi-people'; }
                              return 'bi-circle';
                            };

                            const getIconColor = (action) => {
                              if (action === 'Post') { return 'text-primary'; }
                              else if (action === 'Create') { return 'text-success'; }
                              else if (action === 'Update') { return 'text-info'; }
                              else if (action === 'Delete') { return 'text-danger'; }
                              else if (action === 'Approve') { return 'text-primary'; }
                              return 'text-secondary';
                            };

                            return (
                              <div key={index} className="timeline-item mb-3">
                                <div className="timeline-time">
                                  {/* {showDate && ( */}
                                  <div className="timeline-date">
                                    {currentDate.toLocaleDateString('id-ID', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </div>
                                  {/* )} */}
                                  <div className="timeline-hour">
                                    {currentDate.toLocaleTimeString('id-ID', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false
                                    })}
                                  </div>
                                </div>
                                <div className="timeline-dot-line">
                                  <div className="timeline-dot"></div>
                                  <div className="timeline-line"></div>
                                </div>
                                <div className="timeline-content">
                                  <div className="timeline-box">
                                    <div className="d-flex align-items-start justify-content-between w-100">
                                      <div className="d-flex align-items-start">
                                        <i className={`bi ${getIcon(timeline.event_name, timeline.timeline_action)} me-3 fs-24 ${getIconColor(timeline.timeline_action)}`}></i>
                                        <div className="d-flex flex-column my-2">
                                          <span><strong>{timeline.user_name}</strong> {timeline.timeline_title}</span>
                                          {timeline.timeline_message && (
                                            <p className="text-secondary mt-2 mb-0" style={{ whiteSpace: 'pre-line' }}>{timeline.timeline_message}</p>
                                          )}
                                          {timeline.timeline_files && (
                                            <div className="mt-2">
                                              {JSON.parse(timeline.timeline_files).map((file, f) => (
                                                <div className="mb-2" key={"file-" + f}>
                                                  <i className="bi bi-paperclip me-1" />
                                                  <a className="text-decoration-none text-secondary" href={file.files[0].fileUrl} target="_blank" rel="noreferrer">
                                                    {file.name}
                                                  </a>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {timeline.timeline_link && (
                                        <div className="d-flex align-items-center my-2">
                                          <a href={timeline.timeline_link} target="_blank" rel="noreferrer">
                                            {timeline.event_code}<i className="bi bi-link-45deg ms-2" />
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Tab>
                    <Tab eventKey="subsidiaries" title="Subsidiaries">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fs-24">Subsidiary List</div>
                        {(crmUser.user_department !== 'Sales Admin' && crmUser.user_level !== 'View') && (
                          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => window.open('/client/subsidiary/add/' + id_company, '_blank')}><i className="bi bi-plus-lg me-1" /> Add</button>
                        )}
                      </div>
                      {subcompanies && subcompanies.length ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th className="bg-primary text-light">#</th>
                                <th className="bg-primary text-light">Company Name</th>
                                <th className="bg-primary text-light">Legal Name</th>
                                <th className="bg-primary text-light text-center">Status</th>
                                <th className="bg-primary"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {subcompanies.map((subcompany, s) =>
                                <tr key={"subcompany-" + s} onClick={() => window.location.href = '/client/subsidiary/detail/' + subcompany.id_subcompany}>
                                  <td valign="middle">{s + 1}</td>
                                  <td valign="middle">{subcompany.company_name}</td>
                                  <td valign="middle">{subcompany.company_billing_name}</td>
                                  <td valign="middle" align="center"><span className={"badge bg-" + (subcompany.company_status === 'Active' ? 'success' : 'danger')}>{subcompany.company_status}</span></td>
                                  <td valign="middle" align="right"><a className="btn btn-icon btn-outline-secondary"><i className="bi bi-eye" /></a></td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No data</p>
                      )}
                    </Tab>
                    <Tab eventKey="contacts" title="Contacts">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fs-24">Contact List</div>
                        {(crmUser.user_department !== 'Sales Admin' && crmUser.user_level !== 'View') && (
                          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => window.open('/client/contact/add/' + id_company, '_blank')}><i className="bi bi-plus-lg me-1" /> Add</button>
                        )}
                      </div>
                      {contacts && contacts.length ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th className="bg-primary text-light">#</th>
                                <th className="bg-primary text-light">Name</th>
                                <th className="bg-primary text-light">Email</th>
                                <th className="bg-primary text-light">Phone</th>
                                <th className="bg-primary text-light">Position</th>
                                <th className="bg-primary"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {contacts.map((contact, c) =>
                                <tr key={"contact-" + c} onClick={() => window.location.href = '/client/contact/detail/' + contact.id_contact_person}>
                                  <td valign="middle">{c + 1}</td>
                                  <td valign="middle">{contact.cperson_name}</td>
                                  <td valign="middle">{contact.cperson_email}</td>
                                  <td valign="middle">{contact.cperson_phone}</td>
                                  <td valign="middle">{contact.cperson_position}</td>
                                  <td valign="middle" align="right"><a className="btn btn-icon btn-outline-secondary"><i className="bi bi-eye" /></a></td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No data</p>
                      )}
                    </Tab>
                    <Tab eventKey="quotations" title="Quotations">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fs-24">Quotation List</div>
                        {(crmUser.user_department !== 'Sales Admin' && crmUser.user_level !== 'View') && (
                          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => window.open('/quotation/add/' + id_company, '_blank')}><i className="bi bi-plus-lg me-1" /> Add</button>
                        )}
                      </div>
                      {quotations && quotations.length ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th className="bg-primary text-light">#</th>
                                <th className="bg-primary text-light">Number</th>
                                <th className="bg-primary text-light text-center">Status</th>
                                <th className="bg-primary text-light">Subsidiary</th>
                                <th className="bg-primary text-light">Created Date</th>
                                <th className="bg-primary text-light">SKUs</th>
                                <th className="bg-primary text-light text-end">Amount</th>
                                <th className="bg-primary text-light text-center">Start Period</th>
                                <th className="bg-primary text-light text-center">End Period</th>
                                <th className="bg-primary"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {quotations
                                .sort((a, b) => {
                                  const statusOrder = ['Won', 'Open', 'Draft', 'Expire', 'Freeze', 'Lost', 'Canceled'];
                                  const aIndex = statusOrder.indexOf(a.quotation_status);
                                  const bIndex = statusOrder.indexOf(b.quotation_status);
                                  const aOrder = aIndex === -1 ? statusOrder.length : aIndex;
                                  const bOrder = bIndex === -1 ? statusOrder.length : bIndex;
                                  return aOrder - bOrder;
                                })
                                .map((quotation, q) =>
                                  <tr key={"quotation-" + quotation.id_quotation} onClick={() => window.location.href = '/quotation/detail/' + quotation.id_quotation}>
                                    <td valign="middle">{q + 1}</td>
                                    <td valign="middle">{quotation.quotation_code}</td>
                                    <td valign="middle" align="center"><span className={"badge bg-" + (quotation.quotation_status === 'Won' ? 'success' : 'secondary')}>{quotation.quotation_status}</span></td>
                                    <td valign="middle">{quotation.subcompany_name}</td>
                                    <td valign="middle">{format(quotation.created_on, 'd MMM yyyy')}</td>
                                    <td valign="middle" title={quotation.item_list_names}>{quotation.item_list}</td>
                                    <td valign="middle" align="right">{priceFormat(quotation.quotation_total, quotation.quotation_currency)}</td>
                                    <td valign="middle" align="center">{format(quotation.iou_start_date ? quotation.iou_start_date : quotation.simulation_start_date, 'd MMM yyyy')}</td>
                                    <td valign="middle" align="center">{format(quotation.iou_end_date ? quotation.iou_end_date : quotation.simulation_end_date, 'd MMM yyyy')}</td>
                                    <td valign="middle" align="right"><a className="btn btn-icon btn-outline-secondary"><i className="bi bi-eye" /></a></td>
                                  </tr>
                                )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No data</p>
                      )}
                    </Tab>
                    <Tab eventKey="invoice-requests" title="Invoice Requests">
                      <div className="fs-24 mb-3">Invoice Request List</div>
                      {ious && ious.length ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th className="bg-primary text-light">#</th>
                                <th className="bg-primary text-light">Due Date</th>
                                <th className="bg-primary text-light">Subsidiary</th>
                                <th className="bg-primary text-light">Quotation</th>
                                <th className="bg-primary text-light">Schedule</th>
                                <th className="bg-primary text-light text-end">Amount</th>
                                <th className="bg-primary text-light text-center">Status</th>
                                <th className="bg-primary"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {ious.map((iou, i) =>
                                <tr key={"iou-" + [i]} onClick={() => window.open('/quotation/atb/' + iou.quotation_id + '/' + iou.term_number, '_blank')}>
                                  <td valign="middle">{i + 1}</td>
                                  <td valign="middle">{format(iou.start_term_period, 'd MMM yyyy')}</td>
                                  <td valign="middle">{iou.subcompany_name}</td>
                                  <td valign="middle">{iou.quotation_code}</td>
                                  <td valign="middle">{iou.term_number.replace('Term', 'Schedule')}</td>
                                  <td valign="middle" align="right">{priceFormat(iou.iou_grand_total, iou.iou_currency)}</td>
                                  <td valign="middle" align="center"><span className="badge bg-danger text-light ms-2">Overdue</span></td>
                                  <td valign="middle" align="right"><a className="btn btn-icon btn-outline-secondary"><i className="bi bi-eye" /></a></td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No data</p>
                      )}
                    </Tab>
                    <Tab eventKey="licences" title="Licences">
                      <div className="fs-24 mb-3">Licences List</div>
                      {licences && licences.length ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th className="bg-primary text-light">#</th>
                                <th className="bg-primary text-light">SKU</th>
                                <th className="bg-primary text-light">Domain</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Quantity</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Start Date</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>End Date</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Contract Period</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Payment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {licences.map((licence, l) =>
                                <tr key={"licence-" + [l]}>
                                  <td valign="middle">{l + 1}</td>
                                  <td valign="middle">{licence.product_name}</td>
                                  <td valign="middle">{licence.licence_domain}</td>
                                  <td align="center" valign="middle">{licence.licence_total_seat + ' Unit(s)'}</td>
                                  <td align="center" valign="middle">{format(licence.licence_start_period, 'd MMM yyyy')}</td>
                                  <td align="center" valign="middle">{format(licence.licence_end_period, 'd MMM yyyy')}</td>
                                  <td align="center" valign="middle">{licence.licence_long_period + ' Month(s)'}</td>
                                  <td align="center" valign="middle">{licence.billcycle_payment_period}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No data</p>
                      )}
                    </Tab>
                    <Tab eventKey="contracts" title="Contracts">
                      <div className="fs-24 mb-3">Contracts List</div>
                      {contracts && contracts.length ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th className="bg-primary text-light">#</th>
                                <th className="bg-primary text-light">Contract Name</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Schedules</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Start Date</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>End Date</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Contract Period</th>
                                <th className="bg-primary text-light" style={{ textAlign: 'center' }}>Status</th>
                                <th className="bg-primary text-light"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {contracts.map((contract, c) =>
                                <tr key={"contract-" + [c]} onClick={() => window.open('/client/contract/detail/' + contract.id_contract, '_blank')}>
                                  <td valign="middle">{c + 1}</td>
                                  <td valign="middle">{contract.contract_name}</td>
                                  <td align="center" valign="middle">{contract.contract_schedules}</td>
                                  <td align="center" valign="middle">{format(contract.contract_start_period, 'd MMM yyyy')}</td>
                                  <td align="center" valign="middle">{format(contract.contract_end_period, 'd MMM yyyy')}</td>
                                  <td align="center" valign="middle">{contract.contract_long_period + ' Month(s)'}</td>
                                  <td align="center" valign="middle">{contract.contract_status}</td>
                                  <td valign="middle" align="right"><a className="btn btn-icon btn-outline-secondary"><i className="bi bi-eye" /></a></td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No data</p>
                      )}
                    </Tab>
                  </Tabs>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;


